import React, { useEffect, useState } from 'react';
import { useSkusStore } from '../../../../store/skusStore';
import { createInventoryHold } from '../../../../services/inventoryApi';
import styles from './CreateStockTransferModal.module.css'; // reuse the same modal styling

interface PlaceHoldModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export const PlaceHoldModal: React.FC<PlaceHoldModalProps> = ({ open, onClose, onCreated }) => {
  const [skuId, setSkuId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (!open) {
      setSkuId(''); setQuantity('1'); setReason(''); setNotes('');
      setError(null); setSubmitting(false);
      return;
    }
    if (skus.length === 0) fetchSkus().catch(() => undefined);
  }, [open, skus.length, fetchSkus]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skuId) { setError('Pick a SKU'); return; }
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) { setError('Quantity must be a positive integer'); return; }
    if (!reason.trim()) { setError('Reason is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await createInventoryHold({
        sku_id: Number(skuId),
        quantity: qty,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place hold');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Place Inventory Hold</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.row}>
            <label className={styles.field}>
              <span>SKU <span className={styles.required}>*</span></span>
              <select value={skuId} onChange={(e) => setSkuId(e.target.value)} required>
                <option value="">— Select product —</option>
                {skus.map((s) => (
                  <option key={s.id} value={s.id}>{s.sku_code} — {s.product_name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Quantity <span className={styles.required}>*</span></span>
              <input
                type="number" min="1" value={quantity}
                onChange={(e) => setQuantity(e.target.value)} required
              />
            </label>
          </div>

          <label className={styles.field}>
            <span>Reason <span className={styles.required}>*</span></span>
            <input
              type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Quality investigation, Legal hold, Customer reservation"
              maxLength={200} required
            />
          </label>

          <label className={styles.field}>
            <span>Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={500} />
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Placing Hold…' : 'Place Hold'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlaceHoldModal;
